import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageContext } from '@/i18n/LanguageContext';

export default function Footer() {
  const { t } = useTranslation();
  const { lang } = useLanguageContext();

  // Resolve href for current language
  const href = (path: string) =>
    lang === 'en' ? (path === '/' ? '/en' : `/en${path}`) : path;

  // Category link groups below are a full sitemap of every static page on the
  // site (see docs/screenshots/20260705_footer_final.jpeg review + the route
  // list in src/router/config.tsx). Excluded on purpose: the 404 page, form
  // confirm/complete steps (non-indexable transactional states), dynamic
  // detail pages (blog/:slug, news/:slug), and the /en mirror (language
  // switch already covers it).

  const aboutLinks = [
    { key: 'common.footer.about.personal', href: '/personal' },
    { key: 'common.footer.guide.academy', href: '/japan-butler-academy' },
    { key: 'common.nav.about.overview', href: '/about' },
    { key: 'president.hero.title', href: '/president' },
    { key: 'common.nav.about.philosophy', href: '/philosophy' },
    { key: 'common.nav.about.association', href: '/association' },
    { key: 'common.nav.about.facilities', href: '/facilities' },
    { key: 'common.nav.about.access', href: '/access' },
  ];

  // 執事・執事学校の一般知識ページ(アカデミー自身の紹介とは別カテゴリ)
  const knowledgeLinks = [
    { key: 'common.footer.knowledge.become', href: '/how-to-become-butler' },
    { key: 'common.footer.knowledge.job', href: '/butler-job' },
    { key: 'common.footer.knowledge.school', href: '/butler-school' },
    { key: 'common.footer.knowledge.trainingSchool', href: '/butler-training-school' },
    { key: 'common.footer.knowledge.overseas', href: '/overseas-schools' },
    { key: 'common.footer.knowledge.history', href: '/butler-school-history' },
  ];

  const courseLinks = [
    { key: 'common.footer.courses.list', href: '/courses' },
    { key: 'common.footer.courses.essential', href: '/courses/essential' },
    { key: 'common.footer.courses.advanced', href: '/courses/advanced' },
    { key: 'common.footer.courses.master', href: '/courses/master' },
    { key: 'common.footer.courses.schedule', href: '/schedule' },
    { key: 'common.footer.courses.recruitment', href: '/recruitment' },
    { key: 'common.footer.courses.curriculum', href: '/curriculum' },
    { key: 'common.nav.curriculum.training', href: '/training' },
    { key: 'common.nav.curriculum.materials', href: '/materials' },
    { key: 'common.nav.curriculum.skills', href: '/skills' },
  ];

  const pricingLinks = [
    { key: 'common.nav.pricing.overview', href: '/pricing' },
    { key: 'common.nav.pricing.details', href: '/pricing/details' },
    { key: 'common.nav.pricing.benefits', href: '/benefits' },
    { key: 'common.nav.pricing.certificate', href: '/certificate' },
  ];

  const admissionLinks = [
    { key: 'common.nav.admission.overview', href: '/admission' },
    { key: 'common.nav.admission.application', href: '/application' },
    { key: 'common.nav.admission.apply', href: '/apply' },
    { key: 'common.nav.admission.consultation', href: '/consultation' },
    { key: 'common.nav.admission.seminar', href: '/seminar' },
  ];

  const supportLinks = [
    { key: 'common.footer.support.contact', href: '/contact' },
    { key: 'common.footer.support.faq', href: '/faq' },
    { key: 'common.footer.support.brochure', href: '/brochure' },
    { key: 'common.footer.support.blog', href: '/blog' },
    { key: 'common.header.news', href: '/news' },
    { key: 'common.header.sitemap', href: '/sitemap' },
  ];

  // Corporate links: the general programme + all 8 industry-specific landing
  // pages, shown as their own top-level column (not buried inside Support).
  const corporateLinks = [
    { key: 'common.footer.corporate.dispatch', href: '/corporate' },
    { key: 'common.footer.corporate.brochure', href: '/corporate/brochure' },
    { key: 'common.footer.corporate.industry.hotel', href: '/corporate/hotel' },
    { key: 'common.footer.corporate.industry.residence', href: '/corporate/residence' },
    { key: 'common.footer.corporate.industry.finance', href: '/corporate/finance' },
    { key: 'common.footer.corporate.industry.retail', href: '/corporate/retail' },
    { key: 'common.footer.corporate.industry.luxurySales', href: '/corporate/luxury-sales' },
    { key: 'common.footer.corporate.industry.clinic', href: '/corporate/clinic' },
    { key: 'common.footer.corporate.industry.automotive', href: '/corporate/automotive' },
    { key: 'common.footer.corporate.industry.chauffeur', href: '/corporate/chauffeur' },
  ];

  const legalLinks = [
    { key: 'common.footer.legal.privacy', href: '/privacy' },
    { key: 'common.footer.legal.terms', href: '/terms' },
    { key: 'common.footer.legal.commercial', href: '/commercial-law' },
  ];

  // Butler-network related orgs: these share the academy's own subject matter
  // (butler training/service), so they are shown prominently as a top-level
  // footer column, linking to this site's own introduction page for each org
  // plus the related-organizations hub.
  const relatedButlerLinks = [
    { key: 'common.footer.relatedSites.association', href: '/related/butler-association' },
    { key: 'common.footer.relatedSites.thebutler', href: '/related/the-butler' },
    { key: 'common.footer.relatedSites.butlerbell', href: '/related/butler-bell' },
    { key: 'common.footer.relatedSites.viewAll', href: '/related' },
  ];


  // All 8 category columns share the same heading/list markup and are mapped
  // in one pass below. Order matches a 4-wide grid (row 1: about/courses/
  // pricing/admission, row 2: support/corporate/related/legal). This grid is
  // structurally separate from the organization-info block, so no category
  // ever renders underneath it.
  const categoryColumns: { headingKey: string; links: { key: string; href: string }[] }[] = [
    { headingKey: 'common.nav.about', links: aboutLinks },
    { headingKey: 'common.footer.knowledge.title', links: knowledgeLinks },
    { headingKey: 'common.footer.courses', links: courseLinks },
    { headingKey: 'common.nav.pricing', links: pricingLinks },
    { headingKey: 'common.nav.admission', links: admissionLinks },
    { headingKey: 'common.footer.support', links: supportLinks },
    { headingKey: 'common.footer.corporate', links: corporateLinks },
    { headingKey: 'common.footer.relatedSites', links: relatedButlerLinks },
    { headingKey: 'common.footer.legal', links: legalLinks },
  ];

  return (
    <footer className="bg-primary-900 text-foreground-300 pb-14 md:pb-0">
      {/* Main Footer */}
      <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16 py-16">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
          {/* Organization Info: independent block. On desktop this sits to the
              left of the category grid as its own flex item — never a grid
              track shared with any category column, so no category can ever
              render underneath it. */}
          <div className="lg:w-64 lg:shrink-0">
            <img
              src="/images/brand/logo-tjba-2026.png"
              alt="日本執事アカデミー TJBA"
              className="h-14 w-14 object-contain mb-5"
            />
            <h4 className="text-accent-400 text-[13px] tracking-[0.15em] mb-6 font-heading">
              {t('common.footer.org')}
            </h4>
            <div className="space-y-3 text-[15px] leading-relaxed tracking-[0.03em]">
              <p className="font-heading text-foreground-200 text-sm tracking-[0.06em]">
                {t('common.footer.orgName')}
              </p>
              <p>{t('common.footer.postal')}</p>
              <p>{t('common.footer.address')}</p>
              <p className="pt-2">
                <span className="text-foreground-500">{t('common.footer.tel')}</span>{' '}
                <span className="text-foreground-200">{t('common.footer.telNum')}</span>
              </p>
              <p>
                <span className="text-foreground-500">{t('common.footer.mail')}</span>{' '}
                <a
                  href="mailto:info@butler-academy.jp"
                  className="text-foreground-200 hover:text-accent-400 transition-colors"
                >
                  {t('common.footer.mailAddr')}
                </a>
              </p>
            </div>
          </div>

          {/* Category columns: a self-contained grid, entirely separate from the
              organization-info block above. All 8 categories flow independently
              in their own 4-wide grid (2 rows on desktop). */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10 lg:gap-12 flex-1">
            {categoryColumns.map((col, colIdx) => (
              <div key={colIdx}>
                <h4 className="text-accent-400 text-[13px] tracking-[0.15em] mb-6 font-heading">
                  {t(col.headingKey)}
                </h4>
                <ul className="space-y-2.5">
                  {col.links.map((link, idx) => (
                    <li key={idx}>
                      <Link
                        to={href(link.href)}
                        className="text-[15px] text-foreground-400 hover:text-accent-400 transition-colors tracking-[0.03em]"
                      >
                        {t(link.key)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Bar: related-venture links sit above the final copyright line.
          Privacy/Terms are intentionally NOT repeated here — they already
          live in the "common.footer.legal" category column above, which is
          their correct, single location. */}
      <div className="border-t border-white/8">
        <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16 py-5 flex flex-col items-center gap-3">

          {/* Final line: copyright, last */}
          <p className="text-[13px] text-foreground-500 tracking-[0.05em]">
            &copy; {new Date().getFullYear()} {t('common.footer.copyright')}
          </p>
        </div>
      </div>
    </footer>
  );
}
